import axios from "axios";
import { apiVariable } from "./apiVariable";
import { PublicAssetType } from "../helpers/publicAssetDocuments";
import { getPublicApiError } from "../helpers/publicRequestError";

export interface PublicAssetFile {
  id: string;
  fileName: string;
  type?: string | null;
  startDate?: string | null;
  expirationDate?: string | null;
}

// An independent client keeps public requests free of authentication headers.
const publicClient = axios.create({ withCredentials: false });

export const fetchAvailableAssetFiles = async (
  assetType: PublicAssetType,
  folderId: string,
  signal?: AbortSignal,
): Promise<PublicAssetFile[]> => {
  const endpoint =
    assetType === "truck"
      ? "getAvailableTruckFiles"
      : "getAvailableTrailerFiles";
  const { data } = await publicClient.get<PublicAssetFile[]>(
    `${apiVariable}/${endpoint}/${folderId}`,
    { signal },
  );
  if (!Array.isArray(data)) throw new Error("Invalid document response.");
  return data;
};

export const getPublicDocumentError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      return "This document is not available for public access. Please contact the person who shared this link.";
    }
    if (error.response?.status === 404 || error.response?.status === 410) {
      return "This document or folder is no longer available.";
    }
  }
  return getPublicApiError(
    error,
    "Unable to load this document. Please try again.",
  );
};

export const fetchPublicAssetFile = async (
  file: PublicAssetFile,
  signal?: AbortSignal,
): Promise<Blob> => {
  // The API resolves the stored name, extension and unit from the document ID.
  // Keep the supported route extension for compatibility; no probing is needed.
  const { data } = await publicClient.get<Blob>(
    `${apiVariable}/public/download/${encodeURIComponent(file.id)}/assetFolder/pdf`,
    { responseType: "blob", signal },
  );
  const contentType = data.type.split(";")[0].toLowerCase();
  if (!["application/pdf", "image/jpeg", "image/png"].includes(contentType))
    throw new Error("Unexpected document response.");
  return data;
};
